import React, { createContext } from "react";
import { Alert, AlertTitle, Box, CssBaseline } from "@mui/material";
import { styled, ThemeProvider } from "@mui/material/styles";
import { motion } from "framer-motion";
import SkeletonActionsBar from "./SkeletonActionsBar";
import SkeletonContent from "./SkeletonContent";
import SkeletonDialogs from "./SkeletonDialogs";
import SkeletonFooter from "./SkeletonFooter";
import SkeletonPageHeader from "./SkeletonPageHeader";
import AccountDialog from "./AccountDialog";
import useSkeletonPageState from "../hooks/useSkeletonPageState";
import useAppTheme from "../hooks/useAppTheme";

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

const AnimatedBox = styled(Box)(({ theme }) => ({
  transition: "transform 0.5s ease, background-color 0.5s ease, color 0.5s ease",
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
}));

export default function SkeletonPage({ onSignOut, session, onSessionUpdate }) {
  const state = useSkeletonPageState({ onSignOut, session, onSessionUpdate });
  const { colorMode, theme, mode } = useAppTheme(state.currentTheme);
  const currentYear = new Date().getFullYear();

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AnimatedBox component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}>
          <SkeletonPageHeader
            onToggleTheme={state.handleThemeToggle}
            onOpenAccount={state.openAccountPanel}
            onOpenAdminPanel={state.openAdminPanel}
            onSignOut={onSignOut}
            onLocaleChange={state.handleLocaleChange}
            currentLocale={state.currentLocale}
            currentTheme={state.currentTheme}
            isAdmin={state.isAdmin}
            accountName={(session?.settings?.displayName || session?.email || "U").trim().charAt(0).toUpperCase()}
            accountAvatarUrl={session?.avatarUrl || session?.settings?.avatarUrl || state.accountForm?.avatarUrl || ""}
          />
          {state.updateNotification ? (
            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2 }}>
              <Alert
                severity={state.updateNotification.status === "success" ? "success" : "warning"}
                variant="outlined"
                onClose={state.closeUpdateNotification}
                sx={{ width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto", bgcolor: "background.paper", borderRadius: 3, alignItems: "center" }}
              >
                <AlertTitle sx={{ fontWeight: 700, mb: 0.25 }}>
                  {state.updateNotification.status === "success"
                    ? (state.currentLocale === "en" ? "Update installed successfully" : "Обновление успешно установлено")
                    : (state.currentLocale === "en" ? "Update was not installed" : "Обновление не установлено")}
                </AlertTitle>
                {state.updateNotification.message || (state.updateNotification.status === "success"
                  ? `${state.currentLocale === "en" ? "Current version" : "Текущая версия"}: ${state.updateNotification.currentVersion || state.updateNotification.version}`
                  : (state.currentLocale === "en" ? "The previous version was restored." : "Предыдущая версия восстановлена."))}
              </Alert>
            </Box>
          ) : null}
          {!state.settingsOpen && <SkeletonActionsBar currentLocale={state.currentLocale} awgProfiles={state.awgProfiles} yourIp={session?.yourIp || state.accountForm?.yourIp || ""} analyticsAvailable={!state.awgAnalyticsSummary?.loading && state.awgAnalyticsSummary?.enabled && state.awgAnalyticsSummary?.panelVisible !== false} analyticsDashboardVisible={state.analyticsDashboardVisible} onToggleAnalyticsDashboard={state.toggleAnalyticsDashboard} />}
          <SkeletonContent
            cardScale={state.cardScale}
            showSettings={state.settingsOpen && state.isAdmin}
            activeSection={state.settingsSection}
            onSectionChange={state.setSettingsSection}
            onBack={state.closeAdminPanel}
            users={state.users}
            awgProfiles={state.awgProfiles}
            awgProfilesLoading={state.awgProfilesLoading}
            awgProfilesLoadedOnce={state.awgProfilesLoadedOnce}
            createForm={state.createUserForm}
            passwordDialog={state.passwordDialog}
            deleteUserDialog={state.deleteUserDialog}
            currentUserEmail={session?.email || state.accountForm?.email || ""}
            currentLocale={state.currentLocale}
            loading={state.loading}
            allowRememberMe={state.securitySettings?.allowRememberMe}
            sessionDurationMinutes={state.securitySettings?.sessionDurationMinutes}
            defaultTheme={state.securitySettings?.defaultTheme}
            defaultLocale={state.securitySettings?.defaultLocale}
            securitySettings={state.securitySettings}
            onSecuritySettingsChange={state.handleSecuritySettingsChange}
            onSecuritySettingsSave={state.handleSecuritySettingsSave}
            onSecuritySettingsReset={state.resetSecurityForm}
            onCheckEmailSettings={state.handleCheckEmailSettings}
            onSendTestEmail={state.handleSendTestEmail}
            onProfilesImported={state.refreshAwgProfiles}
            isAdmin={state.isAdmin}
            account={{
              email: state.accountForm?.email || session?.email || "",
              displayName: state.accountForm?.displayName ?? session?.settings?.displayName ?? "",
              initials: ((state.accountForm?.displayName ?? session?.settings?.displayName ?? session?.email ?? "U").trim().charAt(0).toUpperCase() || "U"),
            }}
            onCreateUserFieldChange={state.handleCreateUserFieldChange}
            onOpenNewUser={state.openNewUser}
            awgCreateDialog={state.awgCreateDialog}
            awgEditDialog={state.awgEditDialog}
            awgDeleteDialog={state.awgDeleteDialog}
            awgEventsDialog={state.awgEventsDialog}
            awgShareDialog={state.awgShareDialog}
            awgAnalyticsDialog={state.awgAnalyticsDialog}
            awgAnalyticsSummary={state.awgAnalyticsSummary}
            analyticsDashboardVisible={state.analyticsDashboardVisible}
            onAwgAnalyticsSummaryPeriodChange={state.changeAwgAnalyticsSummaryPeriod}
            onOpenAwgCreateDialog={state.openAwgCreateDialog}
            onCloseAwgCreateDialog={state.closeAwgCreateDialog}
            onOpenAwgEditDialog={state.openAwgEditDialog}
            onCloseAwgEditDialog={state.closeAwgEditDialog}
            onOpenAwgDeleteDialog={state.openAwgDeleteDialog}
            onCloseAwgDeleteDialog={state.closeAwgDeleteDialog}
            onOpenAwgEventsDialog={state.openAwgEventsDialog}
            onCloseAwgEventsDialog={state.closeAwgEventsDialog}
            onCloseAwgShareDialog={state.closeAwgShareDialog}
            onOpenAwgAnalyticsDialog={state.openAwgAnalyticsDialog}
            onCloseAwgAnalyticsDialog={state.closeAwgAnalyticsDialog}
            onAwgAnalyticsDialogPeriodChange={state.openAwgAnalyticsDialog}
            onAwgCreateFieldChange={state.handleAwgCreateFieldChange}
            onAwgEditFieldChange={state.handleAwgEditFieldChange}
            onAwgCreate={state.handleAwgCreate}
            onAwgEditSave={state.handleAwgEditSave}
            onAwgDelete={state.handleAwgDelete}
            onAwgDownload={state.handleAwgDownload}
            onAwgShare={state.handleAwgShare}
            onAwgShareCopy={state.handleAwgShareCopy}
            onAwgShareEmail={state.handleAwgShareEmail}
            onCloseNewUser={state.closeNewUser}
            onCreateUser={state.handleCreateUser}
            onRoleChange={state.handleAdminRoleChange}
            onOpenPasswordDialog={state.openPasswordDialog}
            onClosePasswordDialog={state.closePasswordDialog}
            onPasswordFieldChange={state.handlePasswordFieldChange}
            onPasswordConfirmChange={state.handlePasswordConfirmChange}
            onPasswordSave={state.handleAdminPasswordSave}
            onOpenDeleteDialog={state.openDeleteDialog}
            onCloseDeleteDialog={state.closeDeleteDialog}
            onConfirmDeleteUser={state.handleDeleteUser}
            onOpenChangePassword={() => state.setChangePassOpen(true)}
            onAccountFieldChange={state.handleAccountFieldChange}
            onAccountSave={state.handleAccountSave}
            onAccountReset={state.resetAccountForm}
            accountDirty={state.accountDirty}
            securityDirty={state.securityDirty}
            currentTheme={mode}
          />
          <AccountDialog
            open={state.accountOpen}
            currentLocale={state.currentLocale}
            account={{
              email: state.accountForm?.email || session?.email || "",
              displayName: state.accountForm?.displayName ?? session?.settings?.displayName ?? "",
              initials: ((state.accountForm?.displayName ?? session?.settings?.displayName ?? session?.email ?? "U").trim().charAt(0).toUpperCase() || "U"),
              avatarUrl: state.accountForm?.avatarUrl || "",
              twoFactorEnabled: !!state.accountForm?.twoFactorEnabled,
            }}
            enforceTwoFactor={!!state.securitySettings?.enforceTwoFactor}
            loading={state.loading}
            onClose={state.closeAccountPanel}
            dirty={state.accountDirty}
            onChange={state.handleAccountFieldChange}
            onSave={state.handleAccountSave}
            onReset={state.resetAccountForm}
            onOpenChangePassword={() => state.setChangePassOpen(true)}
            onAvatarUpload={state.handleAvatarUpload}
            onAvatarRemove={state.handleAvatarRemove}
            currentTheme={mode}
          />
          <SkeletonFooter currentYear={currentYear} />
          <SkeletonDialogs
            currentLocale={state.currentLocale}
            changePassOpen={state.changePassOpen}
            confirmChangeOpen={state.confirmChangeOpen}
            oldPassword={state.oldPassword}
            newPassword={state.newPassword}
            confirmPassword={state.confirmPassword}
            loading={state.loading}
            snackbar={state.snackbar}
            isChangeNextDisabled={state.isChangeNextDisabled}
            onCancel={state.handleCancel}
            onOldPasswordChange={(event) => state.setOldPassword(event.target.value)}
            onNewPasswordChange={(event) => state.setNewPassword(event.target.value)}
            onConfirmPasswordChange={(event) => state.setConfirmPassword(event.target.value)}
            onChangePasswordNext={state.handleChangePasswordNext}
            onConfirmChangePassword={state.handleConfirmChangePassword}
            onCloseSnackbar={state.handleCloseSnackbar}
          />
        </AnimatedBox>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
